/**
 * ScopeNote — one line saying what population the figures beside it were computed over.
 *
 * WHY THIS EXISTS
 * ---------------
 * These surfaces used to compute every aggregate over a single tenant while presenting them as
 * the reader's estate. A Super Admin responsible for a whole customer saw one tenant's attestation
 * percentage with nothing on screen to say so, and the Accountability Config tab counted the SAME
 * kind of thing per CUSTOMER — two numbers, two populations, both bare integers.
 *
 * The backend fix makes the scope correct. This makes it VISIBLE, which is the half that stops it
 * silently going wrong again: a number nobody can attribute to a population is a number nobody can
 * catch being wrong. On a surface that reports to an examiner, that attribution is the point.
 *
 * Renders nothing when there is no scope — an older response shape, or a failed load. A missing
 * label must never blank out the figures it describes.
 */
import React from 'react';

export default function ScopeNote({ scope, className = '' }) {
  const text = describe(scope);
  if (!text) return null;
  return (
    <div className={`text-[12px] text-v2-ink-muted ${className}`}>{text}</div>
  );
}

/**
 * The sentence. Written for the person reading the numbers, in the product's own vocabulary —
 * the scope switcher in the header says customer / org / tenant, so this does too rather than
 * inventing a synonym.
 *
 * The single-tenant customer case is called out deliberately. "Covers all 1 tenants" reads like a
 * bug, and more importantly an operator who sees a customer-wide figure that happens to equal a
 * tenant-wide one should be able to tell WHY they match.
 */
function describe(scope) {
  if (!scope || !scope.type) return null;
  const n = Number(scope.tenantCount ?? 0);
  const name = scope.label && String(scope.label).trim() ? String(scope.label).trim() : null;

  // ZERO FIRST. The generic branch below renders "Covering all 0 tenants in Acme Corp", which
  // reads like a bug and — worse — sits directly above a row of zeroes it fails to explain, so the
  // reader is left to guess whether the estate is clean or the page is broken. I wrote a note in
  // this file about "all 1 tenants" reading like a bug and then shipped the 0 case doing exactly
  // that. It is the whole point of this component to say which of the two is true.
  if (n === 0) {
    const who = name || (scope.type === 'CUSTOMER' ? 'This customer'
      : scope.type === 'ORG' ? 'This organization' : 'This tenant');
    return `${who} has no tenants yet, so there is nothing to be accountable for. Figures appear once a tenant exists and agents are registered in it.`;
  }

  if (scope.type === 'CUSTOMER') {
    if (n === 1) {
      return name
        ? `Covering ${name} — a single tenant, so these are the same figures a tenant view would show.`
        : 'Covering your whole customer, which has a single tenant.';
    }
    return name
      ? `Covering all ${n} tenants in ${name}.`
      : `Covering all ${n} tenants in your customer.`;
  }
  if (scope.type === 'ORG') {
    if (n === 1) {
      return name ? `Covering ${name} — a single tenant.` : 'Covering your organization, which has a single tenant.';
    }
    return name ? `Covering all ${n} tenants in ${name}.` : `Covering all ${n} tenants in your organization.`;
  }
  if (scope.type === 'TENANT') {
    return name ? `Covering the ${name} tenant only.` : 'Covering your tenant only.';
  }
  return null;
}
